/**
 * The workspace's own API (backend app/routers/): the signed-in person, their org and its members, the audit trail, the
 * walkthroughs they finished, and the platform admin's views. The Game Builder's calls are in game-builder/api/.
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL;

const opts = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 30000 });

const messageOf = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (!error.response) return "Can't reach the server. Check your connection and try again.";
  return 'Something went wrong. Try again in a moment.';
};

const call = async (request) => {
  try {
    return (await request()).data;
  } catch (error) {
    throw new Error(messageOf(error));
  }
};

// ---- the signed-in person
export const getProfile = () => call(() => axios.get(`${API_BASE}/profile`, opts()));
export const finishTour = (tour) => call(() => axios.put(`${API_BASE}/profile/tours/${encodeURIComponent(tour)}`, null, opts()));
export const replayTours = () => call(() => axios.delete(`${API_BASE}/profile/tours`, opts()));
export const deleteAccount = (password) => call(() => axios.delete(`${API_BASE}/profile`, { ...opts(), data: { password } }));

// ---- their org
export const getOrg = () => call(() => axios.get(`${API_BASE}/org`, opts()));
export const renameOrg = (orgName) => call(() => axios.put(`${API_BASE}/org`, { org_name: orgName }, opts()));
export const listMembers = () => call(() => axios.get(`${API_BASE}/org/users`, opts()));
export const inviteMember = (email, role) => call(() => axios.post(`${API_BASE}/org/invites`, { email, role }, opts()));
export const changeRole = (username, role) =>
  call(() => axios.put(`${API_BASE}/org/users/${encodeURIComponent(username)}`, { role }, opts()));
export const removeMember = (username) => call(() => axios.delete(`${API_BASE}/org/users/${encodeURIComponent(username)}`, opts()));

// ---- the audit trail
export const listAuditEvents = (limit = 100) => call(() => axios.get(`${API_BASE}/audit/events?limit=${limit}`, opts()));

// ---- the platform admin
export const listAllOrgs = () => call(() => axios.get(`${API_BASE}/admin/orgs`, opts()));
export const clearDemoSessions = () => call(() => axios.delete(`${API_BASE}/admin/demo-sessions`, opts()));
export const listDemoRequests = () => call(() => axios.get(`${API_BASE}/admin/demo-requests`, opts()));
export const setDemoRequestStatus = (email, requestId, status) =>
  call(() => axios.put(`${API_BASE}/admin/demo-requests/${encodeURIComponent(email)}/${encodeURIComponent(requestId)}`, { status }, opts()));
export const listDemoSessions = () => call(() => axios.get(`${API_BASE}/admin/demo-sessions`, opts()));
export const createOrg = (orgName, ownerEmail) =>
  call(() => axios.post(`${API_BASE}/admin/orgs`, { org_name: orgName, owner_email: ownerEmail }, opts()));
const adminOrg = (orgId) => `${API_BASE}/admin/orgs/${encodeURIComponent(orgId)}`;
export const getOrgDetails = (orgId) => call(() => axios.get(adminOrg(orgId), opts()));
export const adminRenameOrg = (orgId, orgName) => call(() => axios.put(adminOrg(orgId), { org_name: orgName }, opts()));
export const adminInvite = (orgId, email, role) => call(() => axios.post(`${adminOrg(orgId)}/invites`, { email, role }, opts()));
export const adminChangeRole = (orgId, username, role) =>
  call(() => axios.put(`${adminOrg(orgId)}/users/${encodeURIComponent(username)}`, { role }, opts()));
export const adminRemoveMember = (orgId, username) =>
  call(() => axios.delete(`${adminOrg(orgId)}/users/${encodeURIComponent(username)}`, opts()));

// ---- public
export const getInvite = (token) => call(() => axios.get(`${API_BASE}/invites/${encodeURIComponent(token)}`));
export const acceptInvite = (token, password) => call(() => axios.post(`${API_BASE}/accept-invite`, { token, password }));
export const requestPasswordReset = (email) =>
  call(() => axios.post(`${API_BASE}/request-password-reset`, new URLSearchParams({ username: email }), { timeout: 30000 }));
export const bookDemo = (lead) => call(() => axios.post(`${API_BASE}/public/demo-request`, lead, { timeout: 30000 }));
export const resetPassword = (token, password) =>
  call(() => axios.post(`${API_BASE}/reset-password`, { token, password }, { timeout: 30000 }));
